import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import CategoryPicker from './CategoryPicker';
import TagInput from './TagInput';
import { categoryErrorKey } from '../services/categoryErrors';
import { budgetAPI, tagAPI } from '../services/api';

function ExpenseForm({ expense, onSubmit, onCancel, prefillCategory }) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    date: '',
    description: '',
    category: '',
    amount: '',
    notes: '',
    tags: [],
    subcategory_id: null,
    sonderausgaben_plan_id: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [availablePlans, setAvailablePlans] = useState([]);
  const [tagSuggestions, setTagSuggestions] = useState([]);

  useEffect(() => {
    let cancelled = false;
    tagAPI
      .list()
      .then((response) => {
        if (!cancelled) setTagSuggestions(response.data || []);
      })
      .catch((err) => console.error('Failed to load tags:', err));
    return () => {
      cancelled = true;
    };
  }, []);

  const formatCurrency = (amount) => new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR'
  }).format(amount || 0);

  useEffect(() => {
    if (expense) {
      // Convert sonderausgaben_plan_id to string for the select element
      const planId = expense.sonderausgaben_plan_id ? expense.sonderausgaben_plan_id.toString() : null;

      setFormData({
        date: expense.date ? expense.date.split('T')[0] : '',
        description: expense.description || '',
        // Already the single-letter code — the client no longer translates it.
        category: expense.category || '',
        amount: expense.amount ? expense.amount.toString() : '',
        notes: expense.notes || '',
        tags: expense.tags || [],
        subcategory_id: expense.subcategory_id || null,
        sonderausgaben_plan_id: planId,
      });
    } else {
      // New expense — optionally prefill category from caller (e.g. Dashboard
      // category-card pencil button). prefillCategory is a category code.
      setFormData({
        date: new Date().toISOString().split('T')[0],
        description: '',
        category: prefillCategory || '',
        amount: '',
        notes: '',
        tags: [],
        subcategory_id: null,
        sonderausgaben_plan_id: null,
      });
    }
  }, [expense, prefillCategory]);

  // Load available Sonderausgaben plans when category is 'z'
  useEffect(() => {
    const loadAvailablePlans = async () => {
      if (formData.category === 'z') {
        try {
          const year = new Date(formData.date || new Date()).getFullYear();
          const plans = await budgetAPI.getLinkablePlans(year);
          setAvailablePlans(plans || []);
        } catch (err) {
          console.error('Error loading available plans:', err);
          setAvailablePlans([]);
        }
      } else {
        setAvailablePlans([]);
        setFormData(prev => ({ ...prev, sonderausgaben_plan_id: null }));
      }
    };

    loadAvailablePlans();
  }, [formData.category, formData.date]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!formData.date || !formData.description || !formData.category || !formData.amount) {
      setError(t('errors.fieldsRequired'));
      return;
    }

    if (formData.category === 'z' && !formData.sonderausgaben_plan_id) {
      setError(t('errors.selectPlan'));
      return;
    }

    if (isNaN(parseFloat(formData.amount))) {
      setError(t('errors.amountInvalid'));
      return;
    }

    setLoading(true);
    try {
      const submitData = {
        ...formData,
        amount: parseFloat(formData.amount),
        notes: formData.notes?.trim() || null,
        tags: formData.tags || [],
        subcategory_id: formData.subcategory_id || null,
        sonderausgaben_plan_id: formData.sonderausgaben_plan_id || null,
      };
      await onSubmit(submitData);
    } catch (err) {
      // A 422 from the category guard carries a code; axios' own message
      // ("Request failed with status code 422") tells the user nothing.
      const categoryKey = categoryErrorKey(err);
      setError(categoryKey ? t(categoryKey) : err.message || t('expenseForm.saveError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-primary-900/40 dark:bg-yoru-900/70 backdrop-blur-sm overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-6 w-96 miko-card">
        <div className="mt-1">
          <h3 className="text-lg font-medium miko-heading mb-4">
            {expense ? t('expenseForm.editTitle') : t('expenseForm.addTitle')}
          </h3>

          {error && (
            <div className="mb-4 bg-rose-50 dark:bg-rose-900/30 border border-rose-200 dark:border-rose-700/40 rounded-miko p-3">
              <div className="text-sm text-rose-700 dark:text-rose-300">{error}</div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="date" className="miko-label">{t('expenseForm.date')}</label>
              <input
                type="date"
                id="date"
                name="date"
                value={formData.date}
                onChange={handleInputChange}
                className="miko-input text-sm"
                required
              />
            </div>

            <div>
              <label htmlFor="description" className="miko-label">{t('expenseForm.description')}</label>
              <input
                type="text"
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder={t('expenseForm.descriptionPlaceholder')}
                className="miko-input text-sm"
                required
              />
            </div>

            <div>
              <label htmlFor="category" className="miko-label">{t('expenseForm.category')}</label>
              <CategoryPicker
                value={{
                  categoryCode: formData.category || '',
                  subcategoryId: formData.subcategory_id || null,
                }}
                onChange={({ categoryCode, subcategoryId }) =>
                  setFormData((current) => ({
                    ...current,
                    category: categoryCode,
                    subcategory_id: subcategoryId,
                  }))
                }
              />
            </div>

            {formData.category === 'z' && (
              <div>
                <label htmlFor="sonderausgaben_plan_id" className="miko-label">
                  {t('expenseForm.linkToPlan')}
                </label>
                <select
                  id="sonderausgaben_plan_id"
                  name="sonderausgaben_plan_id"
                  value={formData.sonderausgaben_plan_id}
                  onChange={handleInputChange}
                  className="miko-input text-sm"
                  required
                >
                  <option value="">{t('expenseForm.selectPlanned')}</option>
                  {availablePlans.map(plan => (
                    <option key={plan.id} value={plan.id}>
                      {plan.item_name} ({formatCurrency(plan.estimated_amount)} {t('expenseForm.planned')})
                    </option>
                  ))}
                </select>
                {process.env.NODE_ENV === 'development' && (
                  <div className="text-xs miko-text-muted mt-1">
                    Debug: formData.sonderausgaben_plan_id = "{formData.sonderausgaben_plan_id || 'empty'}"
                    | Available plans: {availablePlans.map(p => `${p.id}:${p.item_name}`).join(', ')}
                  </div>
                )}
                {availablePlans.length === 0 && formData.category === 'z' && (
                  <p className="text-sm miko-text-muted mt-1">
                    {t('expenseForm.noPlansAvailable')} <a href="/budget" className="miko-link">{t('expenseForm.createPlanFirst')}</a>.
                  </p>
                )}
              </div>
            )}

            <div>
              <label htmlFor="amount" className="miko-label">
                {t('expenseForm.amount')} <span className="text-xs miko-text-muted">{t('expenseForm.amountHint')}</span>
              </label>
              <input
                type="number"
                id="amount"
                name="amount"
                value={formData.amount}
                onChange={handleInputChange}
                placeholder="0.00"
                step="0.01"
                className="miko-input text-sm"
                required
              />
            </div>

            <div>
              <label htmlFor="notes" className="miko-label">{t('expenseForm.notesOptional')}</label>
              <textarea
                id="notes"
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                placeholder={t('expenseForm.notesPlaceholder')}
                rows="3"
                className="miko-input text-sm resize-vertical"
              />
            </div>

            <div>
              <label className="miko-label">{t('tags.label')}</label>
              <TagInput
                value={formData.tags}
                onChange={(next) => setFormData((prev) => ({ ...prev, tags: next }))}
                suggestions={tagSuggestions}
              />
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onCancel}
                className="miko-btn-secondary text-sm"
                disabled={loading}
              >
                {t('common.cancel')}
              </button>
              <button
                type="submit"
                disabled={loading}
                className="miko-btn-primary text-sm"
              >
                {loading ? t('expenseForm.submitting') : (expense ? t('expenseForm.submitUpdate') : t('expenseForm.submitAdd'))}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default ExpenseForm;
